import { useEffect, useRef } from "react";

import { APP_BASE_NAME } from "../branding";
import { useStaleClientBundle } from "../hooks/useStaleClientBundle";
import { markStaleClientReloadRequested } from "../versionSkew";
import { planStaleClientToast } from "./StaleClientToastCoordinator.logic";
import { stackedThreadToast, toastManager } from "./ui/toast";
import { t } from "~/i18n";

type StaleClientToastId = ReturnType<typeof toastManager.add>;

/**
 * Config snapshots carry the server's client build through reconnects.
 * Reloading a stale tab loads that build and keeps its RPC calls in step.
 */
export function StaleClientToastCoordinator() {
  const staleClientBundle = useStaleClientBundle();
  const staleKey = staleClientBundle?.staleKey ?? null;
  const toastIdRef = useRef<StaleClientToastId | null>(null);
  const shownKeyRef = useRef<string | null>(null);
  const dismissedAtRef = useRef<number | null>(null);
  const reminderTimerRef = useRef<number | null>(null);

  useEffect(() => {
    function clearReminder() {
      if (reminderTimerRef.current !== null) {
        window.clearTimeout(reminderTimerRef.current);
        reminderTimerRef.current = null;
      }
    }
    function closeToast() {
      const toastId = toastIdRef.current;
      // Cleared before `close` so the `onClose` callback below can tell a
      // programmatic close from a user dismissal: closing fires `onClose`, and
      // treating our own teardown as a dismissal would start a reminder timer
      // for a toast nobody saw.
      toastIdRef.current = null;
      if (toastId !== null) {
        toastManager.close(toastId);
      }
    }
    function showToast(key: string) {
      closeToast();
      shownKeyRef.current = key;
      dismissedAtRef.current = null;
      const toastId = toastManager.add({
        ...stackedThreadToast({
          type: "warning",
          title: t("common/thisPageIsOut_33bvld"),
          description: t("common/refreshThePageTo_ni54pw", { APP_BASE_NAME }),
          timeout: 0,
          actionProps: {
            children: t("settings/refresh_qrq5g7"),
            onClick: () => {
              markStaleClientReloadRequested(key);
              window.location.reload();
            },
          },
        }),
        onClose: () => {
          if (toastIdRef.current !== toastId) {
            return;
          }
          toastIdRef.current = null;
          dismissedAtRef.current = Date.now();
          run();
        },
      });
      toastIdRef.current = toastId;
    }
    function run() {
      clearReminder();
      if (staleKey === null) {
        closeToast();
        shownKeyRef.current = null;
        dismissedAtRef.current = null;
        return;
      }
      const plan = planStaleClientToast(
        {
          staleKey,
          shownKey: shownKeyRef.current,
          dismissedAtMs: dismissedAtRef.current,
        },
        Date.now(),
      );
      if (plan.action === "show") {
        showToast(staleKey);
      } else if (plan.action === "wait") {
        reminderTimerRef.current = window.setTimeout(run, plan.delayMs);
      }
    }

    run();
    return () => {
      clearReminder();
      closeToast();
    };
  }, [staleKey]);

  return null;
}
